import { ReactElement } from 'react';

import { BackrsCard } from '@backrs_ui/common-ui/src/atoms/BackrsCard';
import { BackrsIcons } from '@backrs_ui/common-ui/src/atoms/BackrsIcon';
import { CareerPathScoreSection } from '@backrs_ui/common-ui/src/models/careerPath';
import { BackrsUser } from '@backrs_ui/common-ui/src/models/user';
import { HandleAPICallResponseReturn } from '@backrs_ui/common-ui/src/molecules/AskLubavButtons/AskLubavButtonGroup';

import { Box, SxProps, Typography, Theme } from '@mui/material';

import { DialogNuggetHeader } from './DialogNuggetHeader';
import { NuggetArticle } from './NuggetArticle';
import { NuggetAudio } from './NuggetAudio';
import { NuggetCareerPath } from './NuggetCareerPath/NuggetCareerPath';
import { NuggetDocument } from './NuggetDocument';
import { NuggetEvent } from './NuggetEvent';
import { NuggetGeneratedContent } from './NuggetGeneratedContent';
import { NuggetInstitution } from './NuggetInstitution';
import { NuggetInternship } from './NuggetInternship/NuggetInternship';
import { NuggetOpportunity } from './NuggetOpportunity';
import { NuggetPost } from './NuggetPost';
import { NuggetProfile } from './NuggetProfile/NuggetProfile';
import { NuggetResource } from './NuggetResource';
import { NuggetScholarship } from './NuggetScholarship/NuggetScholarship';
import { NuggetTextResponse } from './NuggetTextResponse';
import { NuggetUXVariant } from './NuggetUITypes';
import { NuggetVideo } from './NuggetVideo';

export function largeNuggetVariantMapper(variant: NuggetUXVariant): {
  component: ReactElement | null;
  icon: BackrsIcons;
} {
  switch (variant.type) {
    case 'resource':
      return {
        component: (
          <NuggetResource
            variant={{
              type: 'LARGE',
              title: variant.title,
              url: variant.url,
              previewImage: variant.previewImage,
            }}
          />
        ),
        icon: 'IconArticle',
      };

    case 'opportunity':
      return {
        component: (
          <NuggetOpportunity
            variant={{ type: 'LARGE', deadline: variant.deadline, mediaUrl: variant.mediaUrl }}
          />
        ),
        icon: 'IconOpportunity',
      };

    case 'event':
      return {
        component: <NuggetEvent variant={{ type: 'SMALL', date: variant.date }} />,
        icon: 'IconEvent',
      };

    case 'video-external':
      return {
        component: <NuggetVideo variant={{ type: 'LARGE', url: variant.url }} />,
        icon: 'IconVideo',
      };

    case 'video-internal':
      return {
        component: <NuggetVideo variant={{ type: 'LARGE', url: variant.url }} />,
        icon: 'IconVideo',
      };

    case 'article':
      return {
        component: (
          <NuggetArticle
            variant={{
              type: 'LARGE',
              articleUrl: variant.articleUrl,
              title: variant.title,
              previewImage: variant.previewImage,
            }}
          />
        ),
        icon: 'IconArticle',
      };

    case 'institution':
      return {
        component: (
          <NuggetInstitution
            variant={{
              type: 'LARGE',
              institutionUrl: variant.url,
              previewImage: variant.previewImage,
            }}
          />
        ),
        icon: 'IconInstitution',
      };

    case 'employer':
      return {
        component: null,
        icon: 'IconInstitution',
      };

    case 'document':
      return {
        component: (
          <NuggetDocument
            variant={{
              type: 'LARGE',
              title: variant.title,
              internalMediaItemUrl: variant.internalMediaItemUrl,
              externalUrl: variant.externalUrl,
            }}
          />
        ),
        icon: 'IconDocument',
      };

    case 'audio':
      return {
        component: (
          <NuggetAudio variant={{ type: 'LARGE', url: variant.url, title: variant.title }} />
        ),
        icon: 'IconAudio',
      };

    case 'text-response':
      return {
        component: (
          <NuggetTextResponse
            variant={{
              type: 'LARGE',
              text: variant.text,
              headshotImg: variant.headshotImg,
              linkToBackrsProfile: variant.linkToBackrsProfile,
              userAbbreviatedName: variant.userAbbreviatedName,
              postDate: variant.postDate,
              postUrl: variant.postUrl,
            }}
          />
        ),
        icon: 'IconTextResponse',
      };

    case 'post':
      return {
        component: (
          <NuggetPost
            variant={{
              type: 'LARGE',
              text: variant.text,
              headshotImg: variant.headshotImg,
              linkToBackrsProfile: variant.linkToBackrsProfile,
              userAbbreviatedName: variant.userAbbreviatedName,
              postDate: variant.postDate,
              postUrl: variant.postUrl,
            }}
          />
        ),
        icon: 'IconTextResponse',
      };

    case 'book':
      return {
        component: (
          <NuggetArticle
            variant={{
              type: 'LARGE',
              title: variant.title,
              articleUrl: variant.url || '',
              previewImage: variant.previewImage,
            }}
          />
        ),
        icon: 'IconBook',
      };

    case 'competency':
      return {
        component: null,
        icon: 'ProsperLogo',
      };

    case 'scholarships':
      return {
        component: (
          <NuggetScholarship
            variant={{ type: 'LARGE', scholarshipInfo: variant.scholarshipInfo }}
          />
        ),
        icon: 'IconScholarship',
      };

    case 'internship':
      return {
        component: (
          <NuggetInternship
            variant={{
              type: 'LARGE',
              internshipInfo: variant.internshipInfo,
              askLubavButtonGroupProps: variant.askLubavButtonGroupProps,
              apiKeyOnClick: variant.apiKeyOnClick,
              handleApiCall: variant.handleApiCall || (() => Promise.resolve(undefined)),
              askLubavButtonResponse: variant.askLubavButtonResponse,
            }}
          />
        ),
        icon: 'IconBriefcase',
      };

    case 'profile-external':
      return {
        component: (
          <NuggetProfile
            variant={{
              type: 'LARGE',
              abbreviatedName: variant.abbreviatedName,
              location: variant.location,
              jobTitle: variant.jobTitle,
              employer: variant.employer,
              school: variant.school,
              profileImageUrl: variant.profileImageUrl,
              profileType: 'EXTERNAL',
              linkToProfile: variant.linkToProfile,
            }}
          />
        ),
        icon: 'IconProfileExternal',
      };

    case 'profile-internal':
      return {
        component: (
          <NuggetProfile
            variant={{
              type: 'LARGE',
              abbreviatedName: variant.abbreviatedName,
              location: variant.location,
              jobTitle: variant.jobTitle,
              employer: variant.employer,
              school: variant.school,
              role: variant.role,
              profileImageUrl: variant.profileImageUrl,
              profileType: 'INTERNAL',
              linkToProfile: variant.linkToProfile,
            }}
          />
        ),
        icon: 'IconProfileInternal',
      };
    case 'career-path':
      return {
        component: (
          <NuggetCareerPath
            careerPathInfo={variant.careerPathInfo}
            isDialogOpen={variant.isDialogOpen}
            tier={variant.tier}
            askLubavButtonGroupProps={variant.askLubavButtonGroupProps}
            askLubavButtonResponse={variant.askLubavButtonResponse}
            apiKeyOnClick={variant.apiKeyOnClick || []}
          />
        ),
        icon: 'IconBriefcase',
      };
    case 'generated-content':
      return {
        component: variant.generatedContentInfo ? (
          <NuggetGeneratedContent variant={variant.generatedContentInfo} />
        ) : (
          <></>
        ),
        icon: 'IconTextResponse',
      };
  }
}

export type HandleShareParams = Readonly<{
  title: string;
  text: string;
  url: string;
  /** a callback to run after the share has completed. Ex: logging to PostHog */
  afterShare?: () => void;
}>;

type DialogNuggetProps = Readonly<{
  title: string;
  titleOnClick?: () => void;
  subTitle?: string;
  description: string;
  columnCategory: string;
  variant: NuggetUXVariant;
  saveToLibrary?: () => void;
  shareParams?: HandleShareParams;
  markAsDone?: () => void;
  customMenuActions?: {
    icon: BackrsIcons;
    text: string;
    onClick: () => void;
  }[];
  hasCompletedNugget?: boolean;
  hasBookmarkedNugget?: boolean;
  fit?: CareerPathScoreSection;
  fitExplainerOnClick?: () => Promise<HandleAPICallResponseReturn | undefined>;
  fitExplainerText?: string;
  user?: BackrsUser | undefined;
}>;

export const DialogNugget = ({
  title,
  titleOnClick,
  subTitle,
  description,
  columnCategory,
  variant,
  saveToLibrary,
  shareParams,
  markAsDone,
  customMenuActions,
  hasCompletedNugget,
  hasBookmarkedNugget,
  fit,
  fitExplainerOnClick,
  fitExplainerText,
  user,
}: DialogNuggetProps) => {
  const { component } = largeNuggetVariantMapper(variant);

  return (
    <BackrsCard sx={containerStyle}>
      <Box>
        <DialogNuggetHeader
          title={title}
          titleOnClick={titleOnClick}
          subTitle={subTitle}
          columnCategory={columnCategory}
          saveToLibrary={saveToLibrary}
          shareParams={shareParams}
          markAsDone={markAsDone}
          customMenuActions={customMenuActions}
          hasCompletedNugget={hasCompletedNugget}
          hasBookmarkedNugget={hasBookmarkedNugget}
          fit={fit}
          fitExplainerOnClick={fitExplainerOnClick}
          fitExplainerText={fitExplainerText}
          user={user}
        />
        <Typography variant="body1" sx={descriptionStyle}>
          {(description || '').replace(/--/g, '—')}
        </Typography>
        {component}
      </Box>
    </BackrsCard>
  );
};

const containerStyle: SxProps<Theme> = {
  pt: 0,
  px: 4,
  pb: 5,
  '@media (max-width: 756px)': {
    px: 2,
  },
};

const descriptionStyle: SxProps<Theme> = {
  mt: 2,
  whiteSpace: 'pre-line',
  lineHeight: '20px',
  fontSize: '16px',
};
